// src/components/AuthForm.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { auth, db, googleProvider } from "../services/firebase";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  addDoc,
  collection,
} from "firebase/firestore";

import API_BASE from "../api/apiBase";

const AuthForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const fromPath = location.state?.from?.pathname;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("login");

  const params = useMemo(
    () => new URLSearchParams(window.location.search),
    [location.search]
  );

  const inviteToken =
    params.get("invite") || localStorage.getItem("inviteToken");

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("homeowner");
  const [companyName, setCompanyName] = useState("");

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3500);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, []);

  const getUserRole = async (uid) => {
    try {
      const snap = await getDoc(doc(db, "users", uid));
      if (snap.exists()) return snap.data()?.role || null;
      return null;
    } catch {
      return null;
    }
  };

  const routeAfterAuth = (resolvedRole) => {
    if (fromPath) {
      navigate(fromPath, { replace: true });
      return;
    }
    if (resolvedRole === "admin") {
      navigate("/admin", { replace: true });
      return;
    }
    if (resolvedRole === "contractor") {
      navigate("/contractor", { replace: true });
      return;
    }
    navigate("/measure", { replace: true });
  };

  const acceptInvite = async (uid, context) => {
    if (!inviteToken || !uid) return;
    try {
      console.log(`Accepting invite (${context}) with token:`, inviteToken);
      const res = await fetch(`${API_BASE}/api/invite/${inviteToken}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ homeownerUid: uid }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.error || "Invite accept failed");
      }
      console.log(`✅ Invite accepted (${context})`);
      localStorage.removeItem("inviteToken");
    } catch (err) {
      console.error("❌ Invite accept failed", err);
    }
  };

  // --------------------------------------------------
  // Google Sign-In
  // --------------------------------------------------
  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const googleUser = result.user;

      // Check if user doc already exists
      const snap = await getDoc(doc(db, "users", googleUser.uid));

      if (!snap.exists()) {
        // New user — redirect to complete profile
        // Store Google user info temporarily so CompleteProfile can use it
        navigate("/complete-profile", {
          replace: true,
          state: {
            uid: googleUser.uid,
            email: googleUser.email,
            name: googleUser.displayName || "",
            fromGoogle: true,
          },
        });
        return;
      }

      // Existing user — check if profile is complete
      const userData = snap.data();
      if (!userData?.role) {
        // Profile incomplete — send back to complete it
        navigate("/complete-profile", {
          replace: true,
          state: {
            uid: googleUser.uid,
            email: googleUser.email,
            name: googleUser.displayName || "",
            fromGoogle: true,
          },
        });
        return;
      }

      // Fully existing user — sign in normally
      await acceptInvite(googleUser.uid, "google");
      const resolvedRole = userData.role;
      showMessage(`Welcome back, ${googleUser.displayName || googleUser.email}!`);
      setTimeout(() => {
        setUser(googleUser);
        routeAfterAuth(resolvedRole);
      }, 1000);

    } catch (err) {
      console.error(err);
      showMessage(err?.message || "Google sign-in failed.", "error");
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // Email / Password Auth
  // --------------------------------------------------
  const handleAuth = async () => {
    setLoading(true);
    try {
      if (mode === "login") {
        const userCred = await signInWithEmailAndPassword(auth, email, password);
        const loggedUser = userCred.user;
        await acceptInvite(loggedUser.uid, "login");
        const resolvedRole = await getUserRole(loggedUser.uid);
        showMessage(`Welcome back, ${loggedUser.email}!`);
        setTimeout(() => {
          setUser(loggedUser);
          routeAfterAuth(resolvedRole);
        }, 1000);
        return;
      }

      if (role === "contractor" && !companyName.trim()) {
        showMessage("Company Name is required for contractors.", "error");
        setLoading(false);
        return;
      }

      const userCred = await createUserWithEmailAndPassword(auth, email, password);
      const createdUser = userCred.user;

      let contractorOrgId = null;

      if (role === "contractor") {
        const orgRef = await addDoc(
          collection(db, "contractorOrganizations"),
          {
            name: companyName.trim(),
            stripeCustomerId: null,
            stripeSubscriptionId: null,
            plan: "base",
            subscriptionStatus: "trialing",
            currentPeriodEnd: null,
            // Extensible fields for future collection
            licenseNumber: null,
            licenseState: null,
            licenseExpiry: null,
            insuranceCarrier: null,
            insurancePolicyNumber: null,
            insuranceExpiry: null,
            serviceArea: [],
            trades: [],
            createdAt: serverTimestamp(),
          }
        );
        contractorOrgId = orgRef.id;
      }

      await setDoc(
        doc(db, "users", createdUser.uid),
        {
          email,
          name,
          address,
          zip,
          phone,
          role,
          contractorOrgId: contractorOrgId || null,
          isOrgAdmin: role === "contractor",
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );

      await acceptInvite(createdUser.uid, "signup");
      showMessage(`Account created for ${email}!`);
      setTimeout(() => {
        setUser(createdUser);
        routeAfterAuth(role);
      }, 1000);

    } catch (err) {
      console.error(err);
      showMessage(err?.message || "Authentication failed.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    showMessage("You have been signed out.");
    navigate("/", { replace: true });
  };

  // --------------------------------------------------
  // Render
  // --------------------------------------------------
  return (
    <div className="auth-form">
      {user ? (
        <div className="auth-status">
          <p>✅ Signed in as <strong>{user.email}</strong></p>
          <button onClick={handleLogout}>Sign Out</button>
        </div>
      ) : (
        <>
          <h3>{mode === "signup" ? "Create Account" : "Sign In"}</h3>

          {/* Google Sign-In Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="google-signin-btn"
          >
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
              style={{ width: 20, height: 20 }}
            />
            Continue with Google
          </button>

          {/* Divider */}
          <div className="auth-divider">
            <div className="auth-divider-line" />
            <span>or</span>
            <div className="auth-divider-line" />
          </div>

          {/* Email / Password */}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />

          <input
            type="password"
            placeholder="Password (min 6 chars)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            required
          />

          {mode === "signup" && (
            <>
              <input
                type="text"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
              <input
                type="text"
                placeholder="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                autoComplete="street-address"
              />
              <input
                type="text"
                placeholder="ZIP Code"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                autoComplete="postal-code"
              />
              <input
                type="text"
                placeholder="Phone (optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
              />
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="homeowner">Homeowner</option>
                <option value="contractor">Contractor</option>
              </select>
              {role === "contractor" && (
                <input
                  type="text"
                  placeholder="Company Name *"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              )}
            </>
          )}

          <button onClick={handleAuth} disabled={loading}>
            {loading
              ? "Processing..."
              : mode === "signup"
              ? "Create Account"
              : "Log In"}
          </button>

          <p style={{ marginTop: 10 }}>
            {mode === "login" ? (
              <span
                onClick={() => setMode("signup")}
                style={{ cursor: "pointer" }}
              >
                Create account instead
              </span>
            ) : (
              <span
                onClick={() => setMode("login")}
                style={{ cursor: "pointer" }}
              >
                Already have an account? Log in
              </span>
            )}
          </p>

          {message && (
            <div className={`auth-message auth-message--${message.type}`}>
              {message.text}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuthForm;
